import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import FacebookIcon from '@mui/icons-material/Facebook';

import GoogleIcon from '../components/GoogleIcon';
import Navbar from '../components/navbar';
import { useAppState } from '../reduxish/appState';


export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const { state } = useAppState();
    const { signInWithEmail, signInWithGoogle, signInWithFacebook } = state.auth!;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const justVerified = searchParams.get('verified') === '1';

    const handleEmailLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!email.trim() || !password.trim()) {
            setError('Please enter both email and password.');
            return;
        }

        setLoading(true);
        try {
            const result = await signInWithEmail(email, password);
            if (result.user) {
                navigate('/');
            }
        } catch (err: any) {
            setError(err.message || 'Login failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setError('');
        try {
            await signInWithGoogle();
        } catch (err: any) {
            setError(err.message || 'Google sign in failed.');
        }
    };

    const handleFacebookLogin = async () => {
        setError('');
        try {
            await signInWithFacebook();
        } catch (err: any) {
            setError(err.message || 'Facebook sign in failed.');
        }
    };

    return (
        <>
            <Navbar />
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center' }}>
                        Sign In
                    </Typography>

                    {justVerified && !error && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Your email has been verified. Please sign in to continue.
                        </Alert>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleEmailLogin} noValidate>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type="password"
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 2, mb: 1, py: 1.5 }}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Sign In'}
                        </Button>
                        <Box sx={{ textAlign: 'right' }}>
                            <Link to="/forgot-password" style={{ color: 'inherit', fontSize: '0.875rem' }}>
                                Forgot password?
                            </Link>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }}>or</Divider>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="google"
                            startIcon={<GoogleIcon />}
                            onClick={handleGoogleLogin}
                            sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem' }}
                        >
                            Continue with Google
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            color="facebook"
                            startIcon={<FacebookIcon />}
                            onClick={handleFacebookLogin}
                            sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem' }}
                        >
                            Continue with Facebook
                        </Button>
                    </Box>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Typography variant="body2">
                            Don't have an account?{' '}
                            <Link to="/signup" style={{ color: 'inherit' }}>
                                Sign Up
                            </Link>
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </>
    );
}
